
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import { PageBase } from 't4core';
import { ActivityPeriodSummary, DateValue, RentalInfo, RentalKPI, T4ListItem } from 't4core';


export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MMMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  styleUrls: ['./executiveSummary.css'],
  selector: 'u4f-executiveSummary',
  templateUrl: './executiveSummary.html',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class ExecutiveSummaryComponent extends PageBase implements OnInit {
  @ViewChild('u4fSummary') u4fSummary: any;
  public hasRevenueShare: boolean = true;
  private _fromDate: Moment = moment().startOf('month');
  private _toDate: Moment = moment().endOf('month');
  public queryParameters: any = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
  public get fromDate(): Moment { return this._fromDate; }
  public set fromDate(value: Moment) {
    if (this._fromDate != value) {
      this._fromDate = value;
      this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
    }
  }
  public get toDate(): Moment { return this._toDate; }
  public set toDate(value: Moment) {
    if (this._toDate != value) {
      this._toDate = value;
      this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
    }
  } 

  public updateParams() {
    this.queryParameters = { from: this._fromDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), to: this._toDate.format("YYYY-MM-DDTHH:mm:ss[Z]"), reportName: "U4F_ExecutiveSummary" };
  }

  constructor(el: ElementRef) {
    super(el, "Use4FreeModule");
  }

  public async ngOnInit() {
    this.loadData();
  }

  public async loadData() {    
    this.fromDate = this.fromDate.clone();

    const today = moment();
    if (this.toDate.isSame(today, 'month') && this.toDate.year() === today.year()) {
      this.toDate = today;
    }

    this.updateParams();
  }

  public getMonthCount(): number {
    return Math.max(1, this.toDate.clone().startOf('month').diff(this.fromDate.clone().startOf('month'), 'months') + 1);
  }

  public rentalLocationGroupChanged(rentalLocationGroup: T4ListItem) {
    this.loadData();
  }

  public sumOfAddonService(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.IncomeFromBookingsExcVat || 0), 0);
  }

  public sumOfBookings(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.AmountOfBookings || 0), 0);
  }

  public sumOfFreeBookings(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.SumOfFree || 0), 0);
  }

  public averageRentOutsPerTrailer(data: any[]): number {
    const trailers = data.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
    const bookings = data.reduce((sum, i) => sum + (i.AmountOfBookings || 0), 0);
    return trailers > 0 ? Math.round(bookings / trailers / this.getMonthCount()) : 0;
  }


  public sumOfAvailabilityOfTrailer(data: any[]): number {
    return (data.reduce((sum, i) => sum + (i.AvailabilityOfTrailers || 0) / 100.00, 0) / data.length);
  }

  public averageRevenuepertrailer(data: any[]): number {
    const trailers = data.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
    const revenue = data.reduce((sum, i) => sum + (i.IncomeFromBookingsExcVat || 0), 0);
    return trailers > 0 ? Math.round(revenue / trailers / this.getMonthCount()) : 0;
  }

  public sumOfPercentageOfBookingWithZero(data: any[]): number {
    return (data.reduce((sum, i) => sum + (i.PercentageOfBookingWithZeroPayment || 0) / 100.00, 0) / data.length);
  }

  public sumOfNetResultPerTrailers(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.NetCostPerTrailer || 0), 0);
  }

  public sumOfAmountOfTrailers(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
  }

  public sumOfTrailerCost(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.OriginalCostOfAllTrailers || 0), 0);
  }

  public sumOfNetResultAfterKickBacks(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.RevenueShare || 0), 0);
  }

  public onDataLoaded(): void {
    const data = this.u4fSummary?.data?.filteredData || [];
    this.hasRevenueShare = this.sumOfNetResultAfterKickBacks(data) !== 0;
  }

  public sumOfRevenueShareInsurance(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.RevenueShareInsurance || 0), 0);
  }

  public sumOfRevenueShareOthers(data: any[]): number {
    return data.reduce((sum, i) => sum + (i.RevenueShareOthers || 0), 0);
  }

  public totalAverageCostPerTrailer(data: any[]): number {
    const trailers = data.reduce((sum, i) => sum + (i.AmountOfTrailers || 0), 0);
    const netCost = data.reduce((sum, i) => sum + (i.NetCostAfterRevenueShare || 0), 0);
    return trailers > 0 ? -Math.round(netCost / trailers / this.getMonthCount()) : 0;
  }

  public sumOfWeeklyCheckupPercentage(data: any[]): number {
    return (data.reduce((sum, i) => sum + (i.WeeklyCheckupPercentage || 0) / 100.00, 0) / data.length);
  }

  public sumOfDiscountWithRevenueShare(data: any[]): number {
    return 1 - (data.reduce((sum, i) => sum + i.NetCostAfterRevenueShare, 0) / data.reduce((sum, i) => sum + i.OriginalCostOfAllTrailers, 0));
  }

}
